/*
 * A&J Comics — theme.js
 *
 * Minimal vanilla JS for storefront interactivity:
 *   - Cart drawer open/close + Shopify cart AJAX
 *   - Product gallery thumbnail switcher
 *   - Product description "more / less" toggle
 *   - Header transparent-on-hero scroll behaviour
 *
 * No framework, no dependencies. Loaded with defer in theme.liquid.
 */

(function () {
  'use strict';

  // ── Helpers ───────────────────────────────────────────────
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const money = (cents) => {
    // Shopify ships money formatting via Liquid filters; for client-rendered
    // updates we lean on Shopify's `Shopify.formatMoney` if present, else
    // fall back to a simple GBP formatter.
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '£{{amount}}');
    }
    const n = (cents / 100).toFixed(2);
    return '£' + n;
  };

  const fetchJSON = async (url, opts = {}) => {
    const res = await fetch(url, {
      ...opts,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(opts.headers || {}) },
    });
    if (!res.ok) throw new Error(`${url} → ${res.status}`);
    return res.json();
  };

  // ── Cart drawer ───────────────────────────────────────────
  const cart = (() => {
    const drawer = $('[data-cart-drawer]');
    if (!drawer) return null;

    const panel = $('[data-cart-panel]', drawer);
    let lastFocus = null;

    const open = () => {
      lastFocus = document.activeElement;
      drawer.dataset.open = 'true';
      drawer.setAttribute('aria-hidden', 'false');
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      // Focus first focusable in panel
      setTimeout(() => {
        const closer = $('[data-cart-close]', panel);
        closer?.focus();
      }, 50);
    };

    const close = () => {
      delete drawer.dataset.open;
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      lastFocus?.focus();
    };

    const refresh = async () => {
      try {
        const data = await fetchJSON('/cart.js');
        // Update count badge
        $$('[data-cart-count]').forEach((el) => {
          el.textContent = data.item_count;
          el.classList.toggle('hidden', data.item_count === 0);
        });
        // Update subtotal
        const subtotal = $('[data-cart-subtotal]');
        if (subtotal) subtotal.textContent = money(data.total_price);
        // Re-fetch the drawer body via section rendering API for HTML accuracy
        const html = await fetch('/?section_id=cart-drawer').then((r) => r.text());
        const fresh = new DOMParser().parseFromString(html, 'text/html');
        const newBody = $('[data-cart-body]', fresh);
        const newFooter = $('[data-cart-footer]', fresh);
        const oldBody = $('[data-cart-body]', drawer);
        const oldFooter = $('[data-cart-footer]', drawer);
        if (newBody && oldBody) oldBody.replaceWith(newBody);
        if (newFooter && oldFooter) {
          oldFooter.replaceWith(newFooter);
        } else if (newFooter && !oldFooter) {
          panel.appendChild(newFooter);
        } else if (!newFooter && oldFooter) {
          oldFooter.remove();
        }
      } catch (err) {
        console.error('[cart] refresh failed:', err);
        // The server-side change may well have SUCCEEDED here -- it is the
        // re-render that failed -- so the drawer can be showing stale numbers.
        // Say so rather than leaving the customer to trust a stale total.
        // Safe despite `cartError` being declared below: this only runs from
        // event handlers, long after the IIFE has finished initialising.
        cartError.show("We could not refresh your basket just now. Reload the page to see the latest.");
      }
    };

    // Cart-mutation errors, surfaced to the customer instead of console-only.
    //
    // `refresh()` and `changeLine()` previously swallowed every failure into
    // `console.error`, so a failed tap on +/-/X did nothing at all with no
    // explanation -- the same "the page ignored me" class of bug as the dead
    // /cart controls. CLAUDE.md Rule 1: nothing may fail silently.
    //
    // The classes live in the LIQUID markup, not here: Tailwind's @source globs
    // only scan *.liquid, so a class that exists solely in an asset .js file is
    // never generated and the banner would render unstyled.
    const cartError = {
      clear() {
        const wrap = document.querySelector('[data-cart-error]');
        const text = document.querySelector('[data-cart-error-text]');
        if (text) text.textContent = '';
        if (wrap) wrap.classList.add('hidden');
      },
      show(msg) {
        const wrap = document.querySelector('[data-cart-error]');
        const text = document.querySelector('[data-cart-error-text]');
        if (!wrap || !text) return;
        // Unhide FIRST, then write - both synchronously, in this order.
        //
        // Order matters for a11y: text inserted into a role="alert" that is
        // still display:none may not be announced, so the region has to be
        // rendered before the content lands.
        //
        // It is deliberately NOT deferred to requestAnimationFrame. An earlier
        // cut did that and shipped an EMPTY red banner: rAF does not fire when
        // the tab is not painting, so the customer saw an error box with no
        // error in it - worse than the silent failure it replaced. Never make
        // an error message depend on a frame callback.
        wrap.classList.remove('hidden');
        text.textContent = msg;
      },
    };

    // `refresh()` re-renders the DRAWER only (`?section_id=cart-drawer`), so a
    // change made from the /cart page would update the cart server-side while
    // the page kept showing the old numbers — a second flavour of "it ignored
    // me". Reload there instead: /cart is a fallback view, not the primary
    // cart UX, so the cost of a full reload is acceptable and it can never
    // drift out of sync with the server.
    const changeLine = async (key, quantity, fromDrawer = true) => {
      cartError.clear();
      try {
        await fetchJSON('/cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: key, quantity }),
        });
        if (fromDrawer) await refresh();
        else window.location.reload();
      } catch (err) {
        console.error('[cart] change failed:', err);
        cartError.show("Sorry, we could not update your basket. Please check your connection and try again.");
      }
    };

    // Triggers — open
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-open]')) {
        e.preventDefault();
        open();
        refresh();
      }
    });

    // Triggers — close
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-close]')) {
        e.preventDefault();
        close();
      }
    });

    // ESC to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.dataset.open) close();
    });

    // Line-item interactions — delegated on `document`, NOT on the drawer.
    //
    // The SAME `aj-line-item` snippet renders in two places: inside this
    // drawer, and on the /cart page via sections/main-cart.liquid — which is
    // OUTSIDE [data-cart-drawer]. Binding here to `drawer` meant every remove
    // and quantity button on /cart was inert, and because those buttons are
    // type="button" they did not even fall back to submitting the cart form.
    // The page silently ignored the customer. Do not re-scope this to the
    // drawer.
    //
    // Read the quantity off the button's OWN [data-line-item] ancestor rather
    // than looking it up by key: on /cart the drawer is also in the DOM, so
    // the same line key exists in BOTH trees and a keyed lookup can read the
    // wrong copy.
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-line-increment], [data-line-decrement], [data-line-remove]');
      if (!btn) return;

      const key = btn.dataset.lineKey;
      const item = btn.closest('[data-line-item]');
      if (!key || !item) return;

      const qty = parseInt($('[data-line-quantity]', item)?.textContent || '0', 10);
      const fromDrawer = drawer.contains(btn);

      if (btn.hasAttribute('data-line-remove')) {
        changeLine(key, 0, fromDrawer);
      } else if (btn.hasAttribute('data-line-increment')) {
        changeLine(key, qty + 1, fromDrawer);
      } else if (qty > 1) {
        changeLine(key, qty - 1, fromDrawer);
      }
    });

    // Intercept add-to-cart forms anywhere on the page
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[data-product-form]');
      if (!form) return;
      e.preventDefault();
      // In-flight guard. Without it, a second tap during the ~1-2s AJAX add
      // silently made quantity 2 -- two identical copies of ONE
      // personalisation feeding order_quantity into send-to-print. (This path
      // is currently dormant -- no form carries data-product-form -- but the
      // guard must ship with the path, not be rediscovered when it wakes.)
      if (form.dataset.cartAddPending) return;
      form.dataset.cartAddPending = '1';
      const submitter = e.submitter || form.querySelector('[type="submit"]');
      if (submitter) submitter.disabled = true;
      const fd = new FormData(form);
      try {
        const res = await fetch('/cart/add.js', { method: 'POST', body: fd });
        if (!res.ok) throw new Error(`add failed: ${res.status}`);
        const added = await res.json();
        await refresh();
        open();
        // Fire analytics last — must never break the cart.
        try {
          const variantId = String(added.variant_id ?? added.id ?? '');
          const productId = String(added.product_id ?? '');
          const qty = Number(added.quantity || 1);
          const priceCents = Number(added.price || 0);
          const value = (priceCents * qty) / 100;
          const currency = window.Shopify?.currency?.active || 'GBP';
          window.fbq?.('track', 'AddToCart', {
            content_ids: [variantId],
            content_type: 'product',
            value: value,
            currency: currency,
          });
          window.gtag?.('event', 'add_to_cart', {
            currency: currency,
            value: value,
            items: [{
              item_id: variantId,
              item_variant: variantId,
              quantity: qty,
              price: priceCents / 100,
              product_id: productId,
            }],
          });
        } catch (_) {
          // Analytics failures never affect the cart.
        }
      } catch (err) {
        console.error('[cart] add failed:', err);
        // Surface a simple toast — refine when toast organism exists
        alert("Sorry, that couldn't be added to your basket.");
      } finally {
        delete form.dataset.cartAddPending;
        if (submitter) submitter.disabled = false;
      }
    });

    return { open, close, refresh };
  })();

  // ── Cart-attribute identity stitch ────────────────────────
  // Fills the hidden attributes[_posthog_*] inputs at submit time, so the
  // Shopify order carries the browser's PostHog ids through checkout to the
  // order webhook, which uses them to stitch the anonymous storefront
  // session to the server-side purchase event. Inputs ship DISABLED in
  // Liquid; they are enabled here only once a real value exists, so an
  // unfilled input can never overwrite a previously-set cart attribute.
  // Runs on the submit event, which fires before the browser serialises the
  // form, so just-in-time values make it into the POST.
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const inputs = form.querySelectorAll('input[data-posthog-cart-attr]');
    if (!inputs.length) return;
    try {
      const ph = window.posthog;
      if (!ph || typeof ph.get_distinct_id !== 'function') return;
      inputs.forEach((input) => {
        const value = input.dataset.posthogCartAttr === 'session_id'
          ? (typeof ph.get_session_id === 'function' ? ph.get_session_id() : '')
          : ph.get_distinct_id();
        if (value) {
          input.value = value;
          input.disabled = false;
        }
      });
    } catch (_) {
      // Identity stitching must never affect the add or the checkout.
    }
  });

  // ── Checkout-click telemetry ──────────────────────────────
  // The numerator for "did the checkout hand-off work". Both cart forms (the
  // /cart page and the drawer footer) submit NATIVELY to /cart with a hidden
  // name="checkout" input, so this listener observes only -- it must never
  // preventDefault, and a telemetry failure must never block the hand-off.
  // The direct-buy PDP fires its own checkout_clicked (source: direct_buy)
  // from its inline script, because its submit goes to /cart/add with
  // return_to=/checkout rather than through these forms.
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.querySelector('input[name="checkout"]')) return;
    try {
      window.posthog?.capture('checkout_clicked', {
        source: form.closest('[data-cart-drawer]') ? 'cart_drawer' : 'cart_page',
      });
    } catch (_) {
      // Telemetry must never affect checkout.
    }
  });

  // ── Product gallery ───────────────────────────────────────
  $$('[data-product-gallery]').forEach((gallery) => {
    const mains = $$('[data-gallery-main]', gallery);
    const thumbs = $$('[data-gallery-thumb]', gallery);

    // Non-initial mains ship as loading="lazy" AND `hidden` (display:none),
    // and a browser never fetches a lazy image that's display:none. Left as
    // is, the first tap on a thumbnail only *starts* that download, so the
    // gallery sits empty until it lands — which reads as "nothing happened"
    // and earns a second tap. (PostHog caught this as dead clicks + rageclicks
    // on the paid PDP.) Flipping loading to "eager" promotes a pending lazy
    // image and starts the fetch even while it's hidden.
    //
    // ⚠️ This used to warm EVERY main at idle, which quietly undid the whole
    // point of lazy+hidden. Measured on a live PDP at 375x812 with zero
    // interaction: all 6 mains downloaded — 1,415KB, of which 1,238KB was for
    // the 5 slides still `hidden` (one is a 427KB animated GIF). Most visitors
    // never open the gallery at all, so that was pure waste on the page we buy
    // paid traffic to.
    //
    // The dead-click fix only ever needed the image the customer is ABOUT to
    // see, so: one slide of lookahead at idle, and the rest on the first sign
    // of gallery intent — which lands well before any of them can be selected.
    // Do NOT restore the warm-everything version; it re-opens the byte problem
    // without making the swap any faster than this does.
    const warm = (m) => { if (m) m.loading = 'eager'; };
    const visibleIndex = Math.max(0, mains.findIndex((m) => !m.classList.contains('hidden')));
    const warmNext = () => warm(mains[visibleIndex + 1]);
    const warmRest = () => mains.forEach(warm);

    const whenIdle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
    if (document.readyState === 'complete') whenIdle(warmNext);
    else window.addEventListener('load', () => whenIdle(warmNext), { once: true });

    // First touch/hover anywhere on the thumb strip means the gallery is about
    // to be used — warm the remainder then. `once` + `passive` so this costs
    // nothing for the majority who never interact with it.
    const thumbStrip = $('[data-gallery-thumbs]', gallery);
    if (thumbStrip) {
      thumbStrip.addEventListener('pointerenter', warmRest, { once: true, passive: true });
      thumbStrip.addEventListener('touchstart', warmRest, { once: true, passive: true });
    }

    thumbs.forEach((thumb) => {
      // If a thumb is touched before the idle pass has run, start that one
      // image immediately rather than waiting for the click to trigger it.
      const warmOne = () => {
        const m = mains[Number(thumb.dataset.galleryIndex)];
        if (m) m.loading = 'eager';
      };
      thumb.addEventListener('pointerenter', warmOne, { once: true, passive: true });
      thumb.addEventListener('touchstart', warmOne, { once: true, passive: true });

      thumb.addEventListener('click', () => {
        const idx = thumb.dataset.galleryIndex;
        mains.forEach((m) => m.classList.toggle('hidden', m.dataset.galleryIndex !== idx));
        thumbs.forEach((t) => {
          const active = t.dataset.galleryIndex === idx;
          t.setAttribute('aria-selected', active ? 'true' : 'false');
          t.classList.toggle('border-ink', active);
          t.classList.toggle('border-rule-light', !active);
        });
      });
    });
  });

  // ── Product description toggle ────────────────────────────
  $$('[data-product-description]').forEach((wrap) => {
    const text = $('[data-product-description-text]', wrap);
    const btn = $('[data-product-description-toggle]', wrap);
    const label = $('[data-product-description-label]', wrap);
    const iconCollapsed = $('[data-product-description-icon-collapsed]', wrap);
    const iconExpanded = $('[data-product-description-icon-expanded]', wrap);
    if (!text || !btn) return;
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      text.classList.toggle('line-clamp-3', expanded);
      if (label) label.textContent = expanded ? 'More' : 'Less';
      if (iconCollapsed) iconCollapsed.hidden = !expanded;
      if (iconExpanded) iconExpanded.hidden = expanded;
    });
  });

  // ── Mobile sticky CTA ─────────────────────────────────────
  // Direct call into handoffToBuilder — NOT a synthetic click on the
  // main CTA. Synthetic clicks on <a> elements via .click() can be
  // flaky in some browsers, especially inside iframes (Shopify theme
  // editor preview is one). Going direct is more reliable.
  $$('[data-mobile-sticky-cta] [data-sticky-cta]').forEach((stickyCta) => {
    stickyCta.addEventListener('click', (e) => {
      e.preventDefault();
      handoffToBuilder();
    });
  });

  // ── Keep the mobile buy bar clear of the cookie-consent banner ──
  //
  // Shopify's consent banner (`#shopify-pc__banner`) and our mobile sticky buy
  // bar are BOTH `position:fixed; bottom:0` — but the banner is z-index
  // 2000000 and the bar is z-40, so on a first visit from a consent region the
  // banner completely buries the sticky Add to basket. Measured on a 375x812
  // viewport: banner y448-812 (45% of the screen, and it has no close X), bar
  // y739-812. The in-page Add to basket, the Hardback variant option and
  // express checkout are all inside that zone too, so a GB/EU visitor on a
  // phone had NO reachable way to buy until they dealt with the banner.
  // Confirmed region-gated: 0 banner interactions from 116 US visitors in 21
  // days of PostHog, vs 9 of 34 GB — so US paid traffic never hit this.
  //
  // We deliberately do NOT shrink, move or auto-dismiss the banner — it is a
  // consent surface and its layout, position and button set must stay exactly
  // as Shopify renders them. (A branding-only re-skin — fonts and colours,
  // with Accept/Decline kept at equal visual weight — lives in theme.css.)
  // We only lift our OWN bar above it, and drop back when it goes. Do not "fix" this by
  // raising the bar's z-index above the banner: that puts our bar over the
  // Accept/Decline buttons, which is worse.
  //
  // If the banner never appears (US traffic, or consent already stored) this
  // does nothing at all.
  (() => {
    const bar = $('[data-direct-sticky-bar]');
    if (!bar) return;

    let lastHeight = -1;
    const sync = () => {
      const banner = document.querySelector('#shopify-pc__banner');
      // getBoundingClientRect().height is 0 while the banner is display:none,
      // which is how it sits between render and reveal — treat that as absent.
      const h = banner ? Math.round(banner.getBoundingClientRect().height) : 0;
      if (h === lastHeight) return; // keep the observer callback cheap
      lastHeight = h;
      bar.style.bottom = h > 0 ? h + 'px' : '';
    };

    sync();

    // Shopify injects and removes the banner asynchronously, and removes it the
    // moment Accept/Decline is pressed — so watch for it rather than measuring
    // once. The memo above keeps this from costing a layout on every unrelated
    // mutation (the cart drawer refresh is a frequent one).
    new MutationObserver(sync).observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', sync, { passive: true });
    window.addEventListener('orientationchange', sync, { passive: true });
  })();

  // ── Scroll-reveal ─────────────────────────────────────────
  // Add `data-reveal` to any element you want to fade up on scroll.
  // CSS handles the transform; this JS adds `data-reveal-in` when
  // the element enters the viewport.
  //
  // Safety net: force-reveal everything after 1.5s in case IO doesn't
  // fire (e.g. inside the Shopify theme editor's preview iframe where
  // IntersectionObserver behaviour is inconsistent). Better to skip
  // the animation than leave content invisible.
  (function scrollReveal() {
    const els = $$('[data-reveal]');
    if (!els.length) return;

    const revealAll = () => els.forEach((el) => el.setAttribute('data-reveal-in', ''));

    if (!('IntersectionObserver' in window)) {
      revealAll();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-reveal-in', '');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    els.forEach((el) => io.observe(el));

    // Safety: if anything is still hidden after 1.5s, just show it.
    setTimeout(revealAll, 1500);
  })();

  // ── Product → builder hand-off ────────────────────────────
  // The product page does NOT show a variant picker — variant selection
  // happens inside the builder. The Create button is a plain link with
  // a base URL; on click, JS appends:
  //   ?product=<shopify_product_id>&handle=<handle>
  // The builder reads these and loads the right template from Supabase.
  //
  // Both the main CTA and the mobile sticky CTA call this same
  // function — no synthetic click() chains, no flakiness.
  function handoffToBuilder() {
    const mainCta = $('[data-product-cta]');
    if (!mainCta) return;
    const ctaHref = mainCta.dataset.productCtaHref;
    if (!ctaHref) {
      // No builder URL configured — fall through to the link's plain href.
      window.location.href = mainCta.href || '/';
      return;
    }
    const productInfo = mainCta.closest('[data-product-info]');
    const productId = productInfo?.dataset.productId;
    const handle = productInfo?.dataset.productHandle;
    // Active Shopify Markets region (ISO country) — passed so the builder shows
    // the exact price/currency the customer saw on the storefront.
    const country = productInfo?.dataset.marketCountry;
    const url = new URL(ctaHref, window.location.origin);
    if (productId) url.searchParams.set('product', productId);
    if (handle) url.searchParams.set('handle', handle);
    if (country) url.searchParams.set('country', country);
    window.location.href = url.toString();
  }

  // Wire main CTA — only intercept when a builder URL is configured.
  // Without a configured URL the link just navigates to its href, which
  // is fine as a fallback.
  $$('[data-product-cta][data-product-cta-href]').forEach((cta) => {
    cta.addEventListener('click', (e) => {
      e.preventDefault();
      handoffToBuilder();
    });
  });

  // ── Mobile nav ────────────────────────────────────────────
  (function mobileNav() {
    const drawer = $('[data-mobile-nav]');
    if (!drawer) return;

    const REVEAL_MS = 340; // must outlast the panel transition in aj-mobile-nav
    let closeTimer = null;
    let lastFocused = null;

    const resetPanels = () => {
      $$('[data-mobile-nav-level="top"]', drawer).forEach((el) => (el.hidden = false));
      $$('[data-mobile-nav-level="drilled"]', drawer).forEach((el) => (el.hidden = true));
    };

    const open = () => {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      lastFocused = document.activeElement;
      drawer.hidden = false;
      document.body.style.overflow = 'hidden';
      // Next frame, so the browser has a chance to paint the closed state and
      // actually animate the transition rather than jumping straight to open.
      requestAnimationFrame(() => drawer.setAttribute('data-open', ''));
      $$('[data-mobile-nav-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const first = drawer.querySelector('button[data-mobile-nav-close]');
      if (first) first.focus({ preventScroll: true });
    };

    // `restoreFocus` is skipped when the drawer closes because we are
    // navigating away -- yanking focus mid-navigation is pointless and can
    // scroll the outgoing page.
    const close = ({ restoreFocus = true } = {}) => {
      drawer.removeAttribute('data-open');
      document.body.style.overflow = '';
      $$('[data-mobile-nav-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (restoreFocus && lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
      // Keep it in the DOM until the slide-out finishes, then hide it from
      // AT and tab order.
      closeTimer = setTimeout(() => {
        drawer.hidden = true;
        resetPanels();
        closeTimer = null;
      }, REVEAL_MS);
    };

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-mobile-nav-open]')) {
        e.preventDefault();
        open();
      } else if (e.target.closest('[data-mobile-nav-close]')) {
        const closer = e.target.closest('[data-mobile-nav-close]');
        // Menu links carry data-mobile-nav-close so the drawer shuts on
        // navigation. Calling preventDefault on them swallows the navigation
        // and the link silently does nothing -- which is exactly what shipped:
        // every top-level mobile menu item was dead. Only block the default
        // for non-links (buttons, the backdrop).
        const isLink = closer.tagName === 'A' && closer.getAttribute('href');
        if (!isLink) e.preventDefault();
        close({ restoreFocus: !isLink });
      } else if (e.target.closest('[data-mobile-nav-drill]')) {
        e.preventDefault();
        const idx = e.target.closest('[data-mobile-nav-drill]').dataset.mobileNavDrill;
        $$('[data-mobile-nav-level="top"]', drawer).forEach((el) => (el.hidden = true));
        $$('[data-mobile-nav-level="drilled"]', drawer).forEach((el) => {
          el.hidden = el.dataset.mobileNavDrilled !== idx;
        });
      } else if (e.target.closest('[data-mobile-nav-back]')) {
        e.preventDefault();
        $$('[data-mobile-nav-level="top"]', drawer).forEach((el) => (el.hidden = false));
        $$('[data-mobile-nav-level="drilled"]', drawer).forEach((el) => (el.hidden = true));
      }
    });

    const FOCUSABLE = 'a[href], button:not([disabled]), summary, input, select, textarea, [tabindex]:not([tabindex="-1"])';

    document.addEventListener('keydown', (e) => {
      const isOpen = drawer.hasAttribute('data-open');
      if (!isOpen) return;

      if (e.key === 'Escape') {
        close();
        return;
      }

      // Trap Tab inside the drawer -- it is aria-modal, so letting focus walk
      // onto the page behind it is a lie to keyboard and screen-reader users.
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, drawer).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  })();

  // ── Modal ─────────────────────────────────────────────────
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-modal-open]');
    if (opener) {
      e.preventDefault();
      const id = opener.dataset.modalOpen;
      const modal = document.getElementById(id);
      if (modal) {
        modal.classList.remove('hidden');
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      }
      return;
    }
    const closer = e.target.closest('[data-modal-close]');
    if (closer) {
      e.preventDefault();
      const modal = closer.closest('[data-modal]');
      if (modal) {
        modal.classList.add('hidden');
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    $$('[data-modal]:not(.hidden)').forEach((m) => {
      m.classList.add('hidden');
      m.setAttribute('aria-hidden', 'true');
    });
    document.body.style.overflow = '';
  });

  // ── Carousels ─────────────────────────────────────────────
  $$('[data-carousel]').forEach((carousel) => {
    const row = $('[data-carousel-row]', carousel);
    const prev = $('[data-carousel-prev]', carousel);
    const next = $('[data-carousel-next]', carousel);
    if (!row) return;
    prev?.addEventListener('click', () => row.scrollBy({ left: -row.clientWidth * 0.8, behavior: 'smooth' }));
    next?.addEventListener('click', () => row.scrollBy({ left: row.clientWidth * 0.8, behavior: 'smooth' }));
  });

  // ── Search input clear ────────────────────────────────────
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-search-clear]');
    if (!btn) return;
    const targetId = btn.dataset.searchClear;
    const input = document.getElementById(targetId);
    if (input) {
      input.value = '';
      input.focus();
      btn.hidden = true;
    }
  });
  document.addEventListener('input', (e) => {
    if (!e.target.matches('input[type=search]')) return;
    const id = e.target.id;
    const clear = document.querySelector(`[data-search-clear="${id}"]`);
    if (clear) clear.hidden = !e.target.value;
  });

  // ── Header adaptive dark/light over [data-header-over-dark] sections ──
  (function header() {
    const header = $('[data-header][data-header-transparent="true"]');
    if (!header) return;
    const darkSections = $$('[data-header-over-dark]');
    const darkLogo = $('[data-header-logo-dark]');
    const lightLogo = $('[data-header-logo-light]');
    const links = $$('[data-header-link]');
    const icons = $$('[data-header-icon]');

    const setDark = (dark) => {
      // Semantic state class for the comic header CSS in sections/header.liquid
      // (thick rule + CTA inversion) — keep alongside the utility toggles.
      header.classList.toggle('hdr-dark', dark);
      header.classList.toggle('bg-ink/75', dark);
      header.classList.toggle('border-white/10', dark);
      header.classList.toggle('bg-white/90', !dark);
      header.classList.toggle('backdrop-blur-md', true);
      header.classList.toggle('border-rule-light', !dark);
      links.forEach((l) => {
        l.classList.toggle('text-white/80', dark);
        l.classList.toggle('hover:text-white', dark);
        l.classList.toggle('text-ink-soft', !dark);
        l.classList.toggle('hover:text-ink', !dark);
      });
      icons.forEach((i) => {
        i.classList.toggle('text-white/80', dark);
        i.classList.toggle('hover:text-white', dark);
        i.classList.toggle('text-ink', !dark);
        i.classList.toggle('hover:text-ink-soft', !dark);
      });
      if (darkLogo && lightLogo) {
        darkLogo.classList.toggle('hidden', dark);
        darkLogo.classList.toggle('block', !dark);
        lightLogo.classList.toggle('hidden', !dark);
        lightLogo.classList.toggle('block', dark);
      }
    };

    // No dark sections on this page — snap to light immediately and bail.
    if (!darkSections.length) { setDark(false); return; }

    // Header is dark when ANY [data-header-over-dark] section overlaps the
    // top edge of the viewport (where the sticky header sits). Measure the
    // real height: a hardcoded 64 missed by the comic rule's 3px border, so
    // at the very top of a page the hero sat at 67px, failed the `< 64`
    // test, and the header stayed light over a dark ground.
    const headerH = (header.offsetHeight || 64) + 4;
    const onScroll = () => {
      let overDark = false;
      for (let i = 0; i < darkSections.length; i++) {
        const r = darkSections[i].getBoundingClientRect();
        if (r.top < headerH && r.bottom > 0) { overDark = true; break; }
      }
      setDark(overDark);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  })();

  // ── Region selector: native <details> + close on outside-click / Escape ──
  (function regionSelector() {
    const pickers = $$('[data-region-selector]');
    if (!pickers.length) return;
    document.addEventListener('click', (e) => {
      pickers.forEach((d) => {
        if (d.open && !d.contains(e.target)) d.open = false;
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      pickers.forEach((d) => {
        if (!d.open) return;
        d.open = false;
        const summary = d.querySelector('summary');
        if (summary) summary.focus();
      });
    });
  })();
})();
